import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const toPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
};

export const getSlidingWindowItems = (currentPage, totalPages, windowSize = 5) => {
  const safeTotal = toPositiveInt(totalPages, 1);
  const safeCurrent = Math.min(safeTotal, toPositiveInt(currentPage, 1));
  const size = toPositiveInt(windowSize, 5);

  if (safeTotal <= size + 2) {
    return Array.from({ length: safeTotal }, (_, index) => ({ type: 'page', page: index + 1 }));
  }

  let start = Math.max(2, safeCurrent - Math.floor(size / 2));
  let end = start + size - 1;
  if (end > safeTotal - 1) {
    end = safeTotal - 1;
    start = Math.max(2, end - size + 1);
  }

  const items = [{ type: 'page', page: 1 }];
  if (start > 2) items.push({ type: 'ellipsis' });
  for (let page = start; page <= end; page += 1) {
    items.push({ type: 'page', page });
  }
  if (end < safeTotal - 1) items.push({ type: 'ellipsis' });
  items.push({ type: 'page', page: safeTotal });
  return items;
};

export const getCompactWindowItems = (currentPage, totalPages) => {
  const safeTotal = toPositiveInt(totalPages, 1);
  const safeCurrent = Math.min(safeTotal, toPositiveInt(currentPage, 1));

  if (safeTotal <= 3) {
    return Array.from({ length: safeTotal }, (_, index) => ({ type: 'page', page: index + 1 }));
  }

  const items = [{ type: 'page', page: 1 }];
  if (safeCurrent > 2) items.push({ type: 'ellipsis' });
  if (safeCurrent !== 1 && safeCurrent !== safeTotal) {
    items.push({ type: 'page', page: safeCurrent });
  }
  if (safeCurrent < safeTotal - 1) items.push({ type: 'ellipsis' });
  items.push({ type: 'page', page: safeTotal });
  return items;
};

const useCompactPager = () => {
  const [compact, setCompact] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : true
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 640px)');
    const sync = () => setCompact(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return compact;
};

const btnClass =
  'inline-flex h-8 w-8 min-w-8 sm:h-9 sm:w-9 sm:min-w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-black/70 text-white disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-black/70';

export default function BookingPagination({ currentPage, totalPages, onPageChange, className = '' }) {
  const compact = useCompactPager();
  const safeTotal = Math.max(1, Number(totalPages) || 1);
  const safeCurrent = Math.min(safeTotal, Math.max(1, Number(currentPage) || 1));
  const items = compact
    ? getCompactWindowItems(safeCurrent, safeTotal)
    : getSlidingWindowItems(safeCurrent, safeTotal);
  const atFirst = safeCurrent === 1;
  const atLast = safeCurrent === safeTotal;

  return (
    <nav className={`flex flex-wrap items-center justify-center gap-1 sm:gap-2 pt-3 min-h-10 max-w-full ${className}`.trim()} aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, safeCurrent - 1))}
        disabled={atFirst}
        className={btnClass}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>

      {items.map((item, index) => {
        if (item.type === 'ellipsis') {
          return (
            <span
              key={`ellipsis-${index}`}
              className="inline-flex h-8 w-6 min-w-6 sm:h-9 sm:w-9 sm:min-w-9 shrink-0 items-center justify-center text-sm text-slate-500 select-none pointer-events-none"
              aria-hidden="true"
            >
              ...
            </span>
          );
        }

        const isActive = item.page === safeCurrent;
        return (
          <button
            key={item.page}
            type="button"
            onClick={() => onPageChange(item.page)}
            className={`inline-flex h-8 w-8 min-w-8 sm:h-9 sm:w-9 sm:min-w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${
              isActive
                ? 'bg-[#a20f0f] text-white'
                : 'border border-white/10 bg-black/70 text-slate-300 hover:text-white'
            }`}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.page}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(safeTotal, safeCurrent + 1))}
        disabled={atLast}
        className={btnClass}
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}
